import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PromptCard from "@/components/PromptCard";
import AdBlock from "@/components/AdBlock";
import api from "@/lib/api";
import { ArrowLeft, Loader2 } from "lucide-react";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { name } = useParams();
  const category = decodeURIComponent(name || "").trim();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const skipRef = useRef(0);
  const sentinelRef = useRef(null);

  useEffect(() => {
    document.title = `${category} — AI Profile Prompts · Giprompthub`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute(
      "content",
      `Curated ${category.toLowerCase()} AI profile prompts. Copy pro-level prompts instantly and paste into Gemini.`,
    );
  }, [category]);

  const loadPage = useCallback(
    async (reset) => {
      setLoading(true);
      try {
        const skip = reset ? 0 : skipRef.current;
        const { data } = await api.get("/prompts", {
          params: { skip, limit: PAGE_SIZE, category },
        });
        setPrompts((prev) => (reset ? data : [...prev, ...data]));
        skipRef.current = skip + data.length;
        setHasMore(data.length === PAGE_SIZE);
      } finally {
        setLoading(false);
      }
    },
    [category],
  );

  useEffect(() => {
    if (!category) return;
    setPrompts([]);
    skipRef.current = 0;
    setHasMore(true);
    loadPage(true);
  }, [category, loadPage]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && hasMore && !loading) loadPage(false);
        });
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadPage]);

  if (!category) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors font-mono-label"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to home
        </Link>

        <div className="mt-4 md:mt-6">
          <div className="font-mono-label text-[#00E5FF]">Category</div>
          <h1
            data-testid="category-heading"
            className="font-display text-4xl md:text-6xl tracking-tight mt-2 leading-[0.95]"
          >
            {category}
          </h1>
          <p className="mt-3 text-white/50 text-sm md:text-base">
            All {category.toLowerCase()} prompts, sorted by newest first.
          </p>
        </div>

        <div className="mt-8">
          <AdBlock variant="banner" testId="ad-category-top" />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div>
            <div className="masonry" data-testid="category-grid-list">
              {prompts.map((p, idx) => (
                <div key={p.id}>
                  <PromptCard prompt={p} />
                  {idx === 4 && (
                    <div className="mt-5">
                      <AdBlock variant="inline" testId="ad-category-inline" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {loading && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            )}
            <div ref={sentinelRef} className="h-10" />

            {!loading && prompts.length === 0 && (
              <div className="text-center py-20 text-white/50" data-testid="category-empty">
                No prompts in this category yet.
              </div>
            )}
            {!hasMore && prompts.length > 0 && (
              <div className="text-center py-10 font-mono-label text-white/40">
                — end of {category} —
              </div>
            )}
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <AdBlock variant="sidebar" testId="ad-category-sidebar" />
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
